import { useState, useEffect, Fragment } from "react";
import { useStore } from "../../contexts/StoreContext";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  createForm,
  updateCustomField,
  deleteCustomField,
} from "../../api/mutations";
import { fetchPasses } from "../../api/queries";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import TrialEndedAlert from "../../components/TrialEndedAlert";
import FormPreview from "./components/FormPreview";
import FormBuilder from "./components/FormBuilder";
import Button from "../../components/Button";
import Header from "../../components/Header";
import { usePlanAlert } from "../../contexts/PlanAlertContext";

export default function NewForm() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { selectedStore } = useStore();
  const queryClient = useQueryClient();
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);
  const [formType, setFormType] = useState(0);
  const [isMarketingFieldEditing, setIsMarketingFieldEditing] = useState(false);
  const [fields, setFields] = useState([
    {
      type: "text",
      label: t("forms.new.initial.name"),
      unique: false,
      required: true,
      placeholder: t("forms.new.initial.placeholder.name"),
      field: "name",
    },
    {
      type: "email",
      label: t("forms.new.initial.email"),
      unique: true,
      required: true,
      placeholder: t("forms.new.initial.placeholder.email"),
      field: "email",
    },
    {
      type: "date",
      label: t("forms.new.initial.birthday"),
      unique: false,
      placeholder: t("forms.new.initial.placeholder.birthday"),
      field: "birthday",
    },
    {
      type: "checkbox",
      label: t("forms.new.initial.marketing"),
      unique: false,
      field: "marketing",
      options: [t("forms.new.initial.marketing")],
    },
  ]);

  const [description, setDescription] = useState(
    `<p style="text-align: center">${t("forms.new.initial.description")}</p>`,
  );
  const [submitText, setSubmitText] = useState(
    t("forms.new.initial.submitText"),
  );
  const [selectedPass, setSelectedPass] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    submitText: "Submit",
    backgroundColor: selectedStore?.background_color,
    textColor: selectedStore?.primary_color,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [originalFieldState, setOriginalFieldState] = useState(null);
  const [showOptionsMap, setShowOptionsMap] = useState({});
  const [optionsState, setOptionsState] = useState({});
  const { showPlanAlert } = usePlanAlert();

  const isFieldUsedInPassLayout = (storeField) => {
    if (!selectedPass?.pass) return false;
    const { header_field, left_field, right_field } = selectedPass.pass;
    return (
      storeField.label === header_field ||
      storeField.label === left_field ||
      storeField.label === right_field
    );
  };

  const isFieldUsedInAnyPassLayout = (storeField) => {
    let isUsed = false;

    passes?.forEach((pass) => {
      const { header_field, left_field, right_field } = pass.pass;
      if (
        storeField.label === header_field ||
        storeField.label === left_field ||
        storeField.label === right_field
      ) {
        isUsed = true;
      }
    });

    console.log("isUsed:", isUsed);

    return isUsed;
  };

  useEffect(() => {
    if (selectedPass?.pass && selectedStore?.custom_fields) {
      selectedStore.custom_fields.forEach((storeField) => {
        if (isFieldUsedInPassLayout(storeField)) {
          addFieldIfNotExists(storeField);
          handleFieldRequirementChange(true, storeField, true);
        }
      });
    }
  }, [selectedPass?.pass, selectedStore?.custom_fields]);

  const addFieldIfNotExists = (storeField) => {
    const fieldExists = fields.some(
      (f) => f.type === storeField.field_type && f.label === storeField.label,
    );

    if (!fieldExists) {
      const newField = {
        type: storeField.field_type,
        label: storeField.label,
        field: storeField.field,
        unique: false,
        required: true,
        options: storeField.options || [],
      };
      setFields((prevFields) => [...prevFields, newField]);
    }
  };

  const handleFieldRequirementChange = (isEnabled, storeField, isRequired) => {
    if (!isEnabled) return;

    const newFields = [...fields];
    const index = newFields.findIndex(
      (f) =>
        (f.type === storeField.type || f.type === storeField.field_type) &&
        f.label === storeField.label,
    );

    if (index !== -1) {
      newFields[index] = {
        ...newFields[index],
        required: isRequired,
      };
      setFields(newFields);
    }
  };

  console.log("EditForm props:", { selectedStore }); // Add this for debugging

  // Reset fields when language changes
  useEffect(() => {
    setFields([
      {
        type: "text",
        label: t("forms.new.initial.name"),
        unique: false,
        required: true,
        placeholder: t("forms.new.initial.placeholder.name"),
        field: "name",
      },
      {
        type: "email",
        label: t("forms.new.initial.email"),
        unique: true,
        required: true,
        placeholder: t("forms.new.initial.placeholder.email"),
        field: "email",
      },
      {
        type: "date",
        label: t("forms.new.initial.birthday"),
        unique: false,
        placeholder: t("forms.new.initial.placeholder.birthday"),
        field: "birthday",
      },
      {
        type: "checkbox",
        label: t("forms.new.initial.marketing"),
        unique: false,
        field: "marketing",
        options: [t("forms.new.initial.marketing")],
      },
    ]);

    setSubmitText(t("forms.new.initial.submitText"));
    setDescription(
      `<p style="text-align: center">${t("forms.new.initial.description")}</p>`,
    );
  }, [language, t]);

  const PREDEFINED_FIELDS = {
    name: {
      type: "text",
      label: t("forms.new.initial.name"),
      unique: false,
      required: true,
      placeholder: t("forms.new.initial.placeholder.name"),
      field: "name",
    },
    email: {
      type: "email",
      label: t("forms.new.initial.email"),
      unique: true,
      required: true,
      placeholder: t("forms.new.initial.placeholder.email"),
      field: "email",
    },
    phone: {
      type: "tel",
      label: t("forms.new.initial.phone"),
      unique: true,
      required: false,
      placeholder: t("forms.new.initial.placeholder.phone"),
      field: "phone",
    },
    birthday: {
      type: "date",
      label: t("forms.new.initial.birthday"),
      unique: false,
      required: false,
      placeholder: t("forms.new.initial.placeholder.birthday"),
      field: "birthday",
    },
    marketing: {
      type: "checkbox",
      label: t("forms.new.initial.marketing"),
      unique: false,
      required: false,
      field: "marketing",
      options: [t("forms.new.initial.marketing")],
    },
  };

  // Fetch passes for the store
  const { data: passes, isLoading: passesLoading } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Auto-select first pass if it's the only one available
  useEffect(() => {
    if (passes?.length === 1 && !selectedPass) {
      const pass = passes[0];
      setSelectedPass(pass);
      setFormType(0); // Ensure form type is set to 0 for pass-based form
    }
  }, [passes, selectedPass]);

  // Populate form data when document is loaded
  useEffect(() => {
    if (selectedStore?.custom_fields) {
      const initialOptionsMap = {};
      selectedStore.custom_fields.forEach((field) => {
        initialOptionsMap[field.id] = ["select", "radio", "checkbox"].includes(
          field.field_type,
        );
      });
      setShowOptionsMap(initialOptionsMap);
    }
  }, [selectedStore?.custom_fields]);

  const createFormMutation = useMutation({
    mutationFn: async (formData) => {
      if (!selectedStore?.id) {
        console.error("Store ID missing:", selectedStore);
        throw new Error("Store ID is missing");
      }

      return createForm({
        storeId: selectedStore.id,
        kind: formData.kind,
        passId: formData.passId,
        description: formData.description,
        submitText: formData.submitText,
        content: formData.content,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        lang: language,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms", selectedStore?.id] });
      // The wizard decides whether this step is done from "setup-status", not
      // from the forms list, so it has to be invalidated too — otherwise
      // /setup reads a stale has_form and sends the merchant straight back here.
      queryClient.invalidateQueries({
        queryKey: ["setup-status", selectedStore?.id],
      });
      if (window.location.pathname.includes("/setup")) {
        navigate("/setup");
      } else {
        navigate("/forms");
      }
    },
    onError: (error) => {
      console.error("Error creating form:", error);
      setError(error.message);
    },
  });

  const updateCustomFieldMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      if (!selectedStore?.id) throw new Error("Store ID is missing");
      return updateCustomField({
        storeId: selectedStore.id,
        fieldId: id,
        data,
      });
    },
    onSuccess: (_, { id, data }) => {
      // Update the fields array to reflect the changes
      setFields((prevFields) =>
        prevFields.map((field) => {
          const matchingStoreField = selectedStore?.custom_fields?.find(
            (f) => f.id === id,
          );
          if (
            matchingStoreField &&
            field.type === matchingStoreField.field_type &&
            field.label === matchingStoreField.label
          ) {
            return {
              ...field,
              label: data.label,
              type: data.field_type,
              options: data.options,
            };
          }
          return field;
        }),
      );

      queryClient.invalidateQueries({
        queryKey: ["document", selectedStore?.id, id],
      });
      setEditingFieldId(null);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const deleteCustomFieldMutation = useMutation({
    mutationFn: async (fieldId) => {
      if (!selectedStore?.id) throw new Error("Store ID is missing");
      return deleteCustomField({
        storeId: selectedStore.id,
        fieldId,
      });
    },
    onSuccess: async (_, fieldId) => {
      // Find the field that was deleted
      const deletedField = selectedStore?.custom_fields?.find(
        (f) => f.id === fieldId,
      );
      if (deletedField) {
        // Remove the field from the form fields if it exists
        setFields((prevFields) =>
          prevFields.filter(
            (f) =>
              !(
                f.type === deletedField.field_type &&
                f.label === deletedField.label
              ),
          ),
        );
      }
      queryClient.invalidateQueries({
        queryKey: ["document", selectedStore?.id, deletedField.id],
      });
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleAddField = () => {
    if (!selectedStore?.features.is_custom_form_fields_enabled) {
      showPlanAlert({
        title: t("common.planAlertTitle"),
        description: t("common.planAlertDescription", {
          plan: "Growth",
        }),
      });
      return;
    }

    setFields([
      ...fields,
      {
        type: "text",
        label: "",
        field: "",
        required: false,
        placeholder: "",
      },
    ]);
  };

  const handleFieldChange = (index, field) => {
    if (index < 0) return;

    const newFields = [...fields];

    console.log(index);
    console.log(field);

    if (field.field != "marketing") {
      // If setting unique to true, also set required to true
      if (field.unique && !field.required) {
        field.required = true;
      }

      // If setting required to false and field is unique, prevent the change
      if (!field.required && newFields[index].unique) {
        return; // Don't allow unchecking required if unique is checked
      }
    }

    newFields[index] = field;
    setFields(newFields);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStore?.id) {
      setError("Store not selected");
      return;
    }
    setIsSaving(true);

    try {
      // Check if store is in trial period (15 days from creation)
      const isInTrialPeriod = () => {
        if (!selectedStore?.trial_expires_at) return false;
        const trialEndDate = new Date(selectedStore.trial_expires_at);
        trialEndDate.setDate(trialEndDate.getDate());
        return new Date() < trialEndDate;
      };

      // Check subscription limits
      if (
        selectedStore?.subscription_status !== "active" &&
        selectedStore?.subscription_status !== "past_due" &&
        selectedStore?.subscription_status !== "trialing" &&
        !isInTrialPeriod() &&
        selectedStore?.setup_completed
      ) {
        setIsTrialEndedAlertOpen(true);
        return;
      }

      const hasUniqueEmailOrPhone = fields.some(
        (field) =>
          (field.type === "email" || field.type === "tel") &&
          field.unique &&
          field.required,
      );

      if (!hasUniqueEmailOrPhone) {
        setError(
          "Please mark at least one Email or Phone field as both unique and required",
        );
        return;
      }

      setError(null);
      const uniqueFields = fields.reduce((acc, field) => {
        const existingField = acc.find(
          (f) => f.type === field.type && f.label === field.label,
        );

        // Only add the field if it doesn't already exist
        if (!existingField) {
          acc.push(field);
        }
        return acc;
      }, []);

      await createFormMutation.mutateAsync({
        storeId: selectedStore.id,
        kind: formType,
        passId: selectedPass?.id,
        description: description,
        submitText: submitText,
        content: {
          fields: uniqueFields,
        },
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        lang: language,
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isSaving) {
    return (
      <div className="bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t("forms.edit.loading")}</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          title={t("forms.new.title")}
          description={t("forms.new.description")}
          rightSection={
            <Button onClick={handleSubmit} isSaving={isSaving}>
              {t("forms.new.button")}
            </Button>
          }
        />
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Form Builder Column */}
          <FormBuilder
            formType={formType}
            description={description}
            submitText={submitText}
            fields={fields}
            formData={formData}
            selectedPass={selectedPass}
            isSaving={isSaving}
            error={error}
            selectedStore={selectedStore}
            passes={passes}
            editingFieldId={editingFieldId}
            originalFieldState={originalFieldState}
            showOptionsMap={showOptionsMap}
            optionsState={optionsState}
            isMarketingFieldEditing={isMarketingFieldEditing}
            PREDEFINED_FIELDS={PREDEFINED_FIELDS}
            onDescriptionChange={setDescription}
            onSubmitTextChange={setSubmitText}
            onFormDataChange={setFormData}
            onSelectedPassChange={setSelectedPass}
            onFieldsChange={setFields}
            handleFieldRequirementChange={handleFieldRequirementChange}
            handleAddField={handleAddField}
            handleFieldChange={handleFieldChange}
            handleSubmit={handleSubmit}
            setEditingFieldId={setEditingFieldId}
            setOriginalFieldState={setOriginalFieldState}
            setShowOptionsMap={setShowOptionsMap}
            setOptionsState={setOptionsState}
            setIsMarketingFieldEditing={setIsMarketingFieldEditing}
            updateCustomFieldMutation={updateCustomFieldMutation}
            deleteCustomFieldMutation={deleteCustomFieldMutation}
            isFieldUsedInPassLayout={isFieldUsedInPassLayout}
            isFieldUsedInAnyPassLayout={isFieldUsedInAnyPassLayout}
            addFieldIfNotExists={addFieldIfNotExists}
            passesLoading={passesLoading}
          />
          {/* Preview Column */}
          {/* translate="no": faithful preview of the customer-facing form;
              machine-translating it would misrepresent what the customer
              actually sees, and keeps Translate out of the description's
              dangerouslySetInnerHTML subtree (see domPatches.js). */}
          <div translate="no">
            <div className="sticky top-20">
              <FormPreview
                selectedStore={selectedStore}
                formData={formData}
                description={description}
                fields={fields}
                submitText={submitText}
                handleFieldChange={handleFieldChange}
              />
            </div>
          </div>
        </div>
      </div>
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
